import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { DataLayer } from '../lib/dataLayer'
import { getCategoryDisplay } from '../config/categoryConfig'
import { ExerciseCategory } from '../types/database'
import { LocalLesson, LocalSource } from '../lib/localDb'

type Exercise = Awaited<ReturnType<typeof DataLayer.getExercise>>
type Card = Awaited<ReturnType<typeof DataLayer.getCards>>[number]

interface ExerciseDetailViewProps {
  exerciseId: string
  onBack: () => void
  onStartStudy: (exerciseId: string) => void
  onStartSequential: (exerciseId: string) => void
  onViewLesson: (lessonId: string) => void
}

const bgColors: Record<string, string> = {
  emerald: 'bg-emerald-50',
  indigo: 'bg-indigo-50',
  purple: 'bg-purple-50',
}

export function ExerciseDetailView({
  exerciseId,
  onBack,
  onStartStudy,
  onStartSequential,
  onViewLesson,
}: ExerciseDetailViewProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [exercise, setExercise] = useState<Exercise | null>(null)
  const [cards, setCards] = useState<Card[]>([])
  const [lessons, setLessons] = useState<LocalLesson[]>([])
  const [source, setSource] = useState<LocalSource | null>(null)
  const [masteredCount, setMasteredCount] = useState(0)

  useEffect(() => {
    async function loadData() {
      if (!user) return

      setLoading(true)
      try {
        const [ex, exCards, exLessonLinks, progress] = await Promise.all([
          DataLayer.getExercise(exerciseId),
          DataLayer.getCardsByExercise(exerciseId),
          DataLayer.getExerciseLessons(exerciseId),
          DataLayer.getUserCardProgress(user.id),
        ])

        if (!ex) {
          setLoading(false)
          return
        }

        setExercise(ex)
        setCards(exCards)

        // Load linked source
        if (ex.source_id) {
          const allSources = await DataLayer.getSources()
          const src = allSources.find(s => s.id === ex.source_id)
          if (src) setSource(src)
        }

        // Load linked lessons
        if (exLessonLinks.length > 0) {
          const allLessons = await DataLayer.getLessons(false)
          const lessonMap = new Map(allLessons.map(l => [l.id, l]))
          const linked = exLessonLinks
            .map(link => lessonMap.get(link.lesson_id))
            .filter((l): l is LocalLesson => l != null)
          setLessons(linked)
        }

        // Count mastered
        const progressMap = new Map(progress.map(p => [p.card_id, p]))
        let mastered = 0
        for (const card of exCards) {
          const p = progressMap.get(card.id)
          if (p && p.status === 'mastered') mastered++
        }
        setMasteredCount(mastered)
      } catch (error) {
        console.error('Failed to load exercise:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [exerciseId, user])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (!exercise) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-gray-500">Exercise not found</p>
        <button onClick={onBack} className="mt-4 text-indigo-600 hover:text-indigo-800">Go back</button>
      </div>
    )
  }

  const category = getCategoryDisplay(exercise.category as ExerciseCategory)
  const progressPercent = cards.length > 0 ? Math.round((masteredCount / cards.length) * 100) : 0
  const bgColor = category ? (bgColors[category.color] || 'bg-gray-50') : 'bg-gray-50'
  const showSequentialButton = exercise.sequential_enabled || exercise.category === 'av-content'

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className={`${bgColor} rounded-xl p-6 mb-6`}>
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/50 transition-colors"
            aria-label="Go back"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{exercise.name}</h1>
              {exercise.level && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-white text-gray-600">
                  {exercise.level}
                </span>
              )}
            </div>
            {exercise.description && (
              <p className="text-gray-600 mt-2">{exercise.description}</p>
            )}
            {source && (
              <div className="mt-3 text-sm text-gray-600 space-y-1">
                {source.metadata?.date && (
                  <p>{new Date(source.metadata.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                )}
                {(source.metadata?.host || source.metadata?.guest) && (
                  <p>
                    {source.metadata.host && <span>Host: {source.metadata.host}</span>}
                    {source.metadata.host && source.metadata.guest && <span> &middot; </span>}
                    {source.metadata.guest && <span>Guest: {source.metadata.guest}</span>}
                  </p>
                )}
                {source.metadata?.duration_minutes && (
                  <p>{source.metadata.duration_minutes} min</p>
                )}
                {source.metadata?.topics && source.metadata.topics.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {source.metadata.topics.map((topic: string) => (
                      <span key={topic} className="px-2 py-0.5 text-xs rounded-full bg-white/60 text-gray-600">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {cards.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-3">
                  <div className="h-2 bg-white/50 rounded-full overflow-hidden flex-1 max-w-xs">
                    <div
                      className="h-full bg-gray-700 transition-all duration-300 rounded-full"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600">
                    {masteredCount}/{cards.length} mastered ({progressPercent}%)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Study Buttons */}
        {cards.length > 0 && (
          <div className="flex gap-3 mt-5 ml-11">
            <button
              onClick={() => onStartStudy(exerciseId)}
              className="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              Study
            </button>
            {showSequentialButton && (
              <button
                onClick={() => onStartSequential(exerciseId)}
                className="px-6 py-2.5 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Study Sequential
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lessons Section */}
      {lessons.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lessons</h2>
          <div className="space-y-3">
            {lessons.map((lesson) => (
              <button
                key={lesson.id}
                onClick={() => onViewLesson(lesson.id)}
                className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{lesson.title}</h3>
                    {lesson.description && (
                      <p className="text-sm text-gray-500 mt-1">{lesson.description}</p>
                    )}
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Cards summary */}
      {cards.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Cards</h2>
          <p className="text-sm text-gray-500">
            {cards.length} card{cards.length !== 1 ? 's' : ''} in this exercise
          </p>
        </section>
      )}

      {cards.length === 0 && lessons.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>No content in this exercise yet.</p>
        </div>
      )}
    </div>
  )
}
